 
 
import { Component, OnInit, Input, Output, EventEmitter, input, signal, effect, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { DateTimeUtilService } from '../../../../../utilities/date-time-util.service';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatIconModule } from '@angular/material/icon';
import { DateFormatter } from '../../../../../utilities/date-formatter.util';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-lt-datetimepicker',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
    MatTimepickerModule,
    MatIconModule,
    MatButtonModule,
  ],
  providers: [
    DateTimeUtilService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LTDateTimePickerComponent),
      multi: true,
    },
  ],
  template: `
    <div class="datetime-container">
      <form [formGroup]="dateFormGroup" class="date-form">
        <mat-form-field [appearance]="fillMode" class="full-width">
          @if(label)
          {
            <mat-label>{{label}}</mat-label>
          }
          <input
            matInput
            formControlName="dateControl"
            type="datetime-local"
            step="{{step}}"
            [min]="minDate() ? dateToInputValue(minDate()) : ''"
            [max]="maxDate() ? dateToInputValue(maxDate()) : ''"
            />
           @if(dateFormGroup.get('dateControl')?.value && clearButton){
            <button matSuffix matIconButton aria-label="Clear" (click)="clearDate($event)">
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>
      </form>
    </div>
  `,
  styles: `
    .mdc-text-field__input::-webkit-calendar-picker-indicator {
      display: block !important;
    }
    .full-width {
      width: 100%;
    }
    .date-form {
      min-width: 150px;
      max-width: 500px;
      width: 100%;
    }

    .datetime-container {
      .mat-mdc-form-field .mat-notched-outline {
        border: none !important;
      }

      .mat-mdc-form-field .mdc-notched-outline__notch {
        border-right-style: hidden;
      }
    }
  `,
})
export class LTDateTimePickerComponent implements ControlValueAccessor, OnInit {
  // NOTE: internal format of datetime-local is ALWAYS yyyy-MM-DDTHH:mm
  // ALSO: timesones not handled in datetime-local input, it uses the local timezone of the browser

  //#region Inputs
  @Input() fillMode: 'outline' | 'fill' = 'outline';
  @Input() placeholder = 'Enter a date';
  @Input() clearButton = true;
  @Input() label = 'Date';
  @Input() step = 1;

  required = input(false);

  public toISOValidDateInput(date: Date): string {
    return date.toISOString().slice(0, 16);
  }

  //#region MinDate
  private _minDate = signal<Date | null>(null);
  @Input() set min(value: Date | null) {
    this._minDate.set(value);
  }
  get minDate() {
    return this._minDate;
  }
  //#endregion

  //#region MaxDate
  private _maxDate = signal<Date | null>(null);
  @Input() set max(value: Date | null) {
    this._maxDate.set(value);
  }
  get maxDate() {
    return this._maxDate;
  }
  //#endregion

  //#region format
  private _format = signal('YYYY-MM-DDTHH:mm');
  showFormattedHint = false;
  @Input() set format(value: string) {
    if (value) {
      this._format.set(value);
      this.showFormattedHint = true;
    }
  }
  get format(): string {
    return this._format();
  }
  //#endregion

  //#region Value
  private _value = signal<Date | null>(null);
  @Input() set value(value: Date | null) {
    this._value.set(value);
    if (this.dateFormGroup) {
      this.dateFormGroup.get('dateControl')?.setValue(this.dateToInputValue(value), { emitEvent: false });
    }
  }
  get value(): Date | null {
    return this._value();
  }
  //#endregion

  //#region Disabled
  @Input() set disabled(isDisabled: boolean) {
    if (isDisabled) {
      this.dateFormGroup.disable();
    } else {
      this.dateFormGroup.enable();
    }
  }
  get disabled(): boolean {
    return this.dateFormGroup.disabled;
  }
  //#endregion

  //#endregion

  // Form control for the DateTimePicker
  dateFormGroup: FormGroup;

  // ouput
  @Output() valueChange = new EventEmitter<Date | null>();

  // ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  ngOnInit() {
    if (this._value){
      this.dateFormGroup.get('dateControl')?.setValue(
        this.dateToInputValue(this._value()),
      );
    }

    this.dateFormGroup.get('dateControl')?.valueChanges.subscribe(inputValue => {
      const dateValue = this.inputValueToDate(inputValue);
      this._value.set(dateValue);
      this.onChange(dateValue);
      this.valueChange.emit(dateValue);
    });
  }

  constructor() {
    this.dateFormGroup = new FormGroup({
      dateControl: new FormControl(''), // Initial value
    });

    effect(() => {
      this._format(); // Track format changes
      this.showFormattedHint = true;
    });
  }

  /**
   * Converts a Date object to ISO string format required by datetime-local input
   * Format: YYYY-MM-DDThh:mm
   */
  dateToInputValue(date: Date | null): string {
    if (!date) return '';

    // Create the ISO string and remove seconds and milliseconds
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    // Format: YYYY-MM-DDThh:mm
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  /**
   * Parses an input value from the datetime-local input into a Date object
   */
  inputValueToDate(value: string): Date | null {
    if (!value) return null;
    return new Date(value);
  }

  // Format display date based on user format
  formatDisplayDate(date: Date): string {
    if (!date) return '';
    return DateFormatter.formatDate(date, this.format);
  }

  // Clear the date
  clearDate(event: Event): void {
    event.stopPropagation();
    this.dateFormGroup.get('dateControl')?.setValue(null);
    this._value.set(null);
    this.onChange(null);
    this.valueChange.emit(null);
  }

  writeValue(value: Date): void {
    this._value.set(value);
    const inputValue = this.dateToInputValue(value);
    if (this.dateFormGroup)
    {
      this.dateFormGroup.get('dateControl')?.setValue(inputValue, {emitEvent: false});
    }
    this.valueChange.emit(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateFormGroup.disable();
    } else {
      this.dateFormGroup.enable();
    }
  }

}
